import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createInitialState, validateToken, validateUser } from './schemas';
// Create the store
export const useAuthStore = create()(persist((set, get) => (Object.assign(Object.assign({}, createInitialState()), { 
    // Authentication actions
    setTokens: (tokens) => {
        try {
            const validatedTokens = validateToken(tokens);
            set((state) => ({
                auth: Object.assign(Object.assign({}, state.auth), { tokens: validatedTokens, isAuthenticated: true, lastRefresh: Date.now(), error: undefined })
            }));
        }
        catch (error) {
            console.error('Invalid tokens provided:', error);
            set((state) => ({
                auth: Object.assign(Object.assign({}, state.auth), { error: `Invalid token format: ${error instanceof Error ? error.message : String(error)}` })
            }));
        }
    }, setUser: (user) => {
        try {
            const validatedUser = validateUser(user);
            set((state) => ({
                auth: Object.assign(Object.assign({}, state.auth), { user: validatedUser, error: undefined })
            }));
        }
        catch (error) {
            console.error('Invalid user data provided:', error);
            set((state) => ({
                auth: Object.assign(Object.assign({}, state.auth), { error: `Invalid user data: ${error instanceof Error ? error.message : String(error)}` })
            }));
        }
    }, setLoginHint: (loginHint) => {
        set((state) => ({
            auth: Object.assign(Object.assign({}, state.auth), { loginHint })
        }));
    }, setAuthenticating: (isLoading) => {
        set((state) => ({
            auth: Object.assign(Object.assign({}, state.auth), { isLoading })
        }));
    }, setInitialized: (isInitialized) => {
        set((state) => ({
            auth: Object.assign(Object.assign({}, state.auth), { isInitialized })
        }));
    }, setAuthError: (error) => {
        set((state) => ({
            auth: Object.assign(Object.assign({}, state.auth), { error, isLoading: false })
        }));
    }, clearAuth: () => {
        set((state) => ({
            auth: Object.assign(Object.assign({}, state.auth), { isAuthenticated: false, tokens: undefined, user: undefined, lastRefresh: undefined, error: undefined, isLoading: false })
        }));
    }, 
    // Token validation helpers
    isTokenValid: () => {
        const { tokens } = get().auth;
        if (!tokens)
            return false;
        return Date.now() < tokens.expiresAt;
    }, isTokenExpiringSoon: (minutesThreshold = 5) => {
        const { tokens } = get().auth;
        if (!tokens)
            return true;
        const thresholdMs = minutesThreshold * 60 * 1000;
        return Date.now() + thresholdMs >= tokens.expiresAt;
    }, 
    // UI actions
    setCurrentRoute: (route) => {
        set((state) => ({
            ui: Object.assign(Object.assign({}, state.ui), { currentRoute: route })
        }));
    }, setUILoading: (isLoading) => {
        set((state) => ({
            ui: Object.assign(Object.assign({}, state.ui), { isLoading })
        }));
    }, addUIError: (error) => {
        set((state) => ({
            ui: Object.assign(Object.assign({}, state.ui), { errors: [...state.ui.errors, error] })
        }));
    }, clearUIErrors: () => {
        set((state) => ({
            ui: Object.assign(Object.assign({}, state.ui), { errors: [] })
        }));
    }, 
    // MSAL integration placeholders (will be implemented by adapter)
    initializeAuth: async (loginHint) => {
        // This will be overridden by the MSAL adapter
        console.warn('initializeAuth not implemented - use MSAL adapter');
    }, refreshTokens: async (scopes = ['user.read']) => {
        // This will be overridden by the MSAL adapter
        console.warn('refreshTokens not implemented - use MSAL adapter');
        return '';
    }, getValidToken: async (scopes) => {
        // This will be overridden by the MSAL adapter
        console.warn('getValidToken not implemented - use MSAL adapter');
        return '';
    }, logout: async () => {
        // This will be overridden by the MSAL adapter
        console.warn('logout not implemented - use MSAL adapter');
    } })), {
    name: 'outlook-addin-auth-store',
    version: 1,
    storage: createJSONStorage(() => {
        // Use sessionStorage for Office Add-in compatibility
        // This ensures data persists during the session but not across browser restarts
        return typeof window !== 'undefined' ? sessionStorage : {
            getItem: () => null,
            setItem: () => { },
            removeItem: () => { }
        };
    }),
    partialize: (state) => ({
        // Only persist user info and login hint for security
        // Tokens should be refreshed on each session
        auth: {
            user: state.auth.user,
            loginHint: state.auth.loginHint,
            isInitialized: state.auth.isInitialized
        },
        ui: {
            currentRoute: state.ui.currentRoute
        }
    }),
    onRehydrateStorage: () => {
        return (state, error) => {
            if (error) {
                console.error('Failed to rehydrate auth store:', error);
            }
            else if (state) {
                console.log('Auth store rehydrated successfully');
                // Clear authentication state on rehydration since tokens aren't persisted
                state.clearAuth();
            }
        };
    }
}));
// Selectors for common state access patterns
export const useAuthState = () => useAuthStore((state) => state.auth);
export const useUIState = () => useAuthStore((state) => state.ui);
export const useIsAuthenticated = () => useAuthStore((state) => state.auth.isAuthenticated);
export const useCurrentUser = () => useAuthStore((state) => state.auth.user);
export const useAuthError = () => useAuthStore((state) => state.auth.error);
export const useIsLoading = () => useAuthStore((state) => state.auth.isLoading);
// Action selectors
export const useAuthActions = () => {
    const store = useAuthStore();
    return {
        setTokens: store.setTokens,
        setUser: store.setUser,
        setLoginHint: store.setLoginHint,
        setAuthenticating: store.setAuthenticating,
        setInitialized: store.setInitialized,
        setAuthError: store.setAuthError,
        clearAuth: store.clearAuth,
        isTokenValid: store.isTokenValid,
        isTokenExpiringSoon: store.isTokenExpiringSoon
    };
};
